import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const paymentOpsKeys = {
  all: ['payment-ops'] as const,
  detail: (paymentId: string) =>
    [...paymentOpsKeys.all, 'detail', paymentId] as const,
  status: (paymentId: string) =>
    [...paymentOpsKeys.all, 'status', paymentId] as const,
  // Conditional spread keeps invalidateQueries prefix matching working —
  // a trailing `undefined` would defeat React Query's partialDeepEqual.
  list: (filters?: PaymentsListFilters) =>
    filters
      ? ([...paymentOpsKeys.all, 'list', filters] as const)
      : ([...paymentOpsKeys.all, 'list'] as const),
  refunds: (paymentId: string) =>
    [...paymentOpsKeys.all, 'refunds', paymentId] as const,
};

// --- Types ---
interface Payment {
  id: string;
  user_profile_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  external_id?: string;
  [key: string]: unknown;
}

interface InitiatePaymentData {
  event_registration_id: string;
  amount: number;
  currency?: string;
  payment_method_id?: string;
  return_url?: string;
  cancel_url?: string;
}

interface CapturePaymentData {
  external_order_id: string;
}

interface ExecutePaymentData {
  payment_id?: string;
  external_order_id?: string;
  [key: string]: unknown;
}

interface PaymentsListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  user_profile_id?: string;
  event_id?: string;
}

interface RefundData {
  amount?: number;
  reason?: string;
}

interface PaymentRefund {
  id: string;
  payment_id: string;
  amount: number;
  reason?: string;
  status: string;
  [key: string]: unknown;
}

interface AdminUpdateStatusData {
  status: string;
  notes?: string;
}

// --- Query Hooks ---
export function usePaymentDetails(paymentId: string) {
  return useQuery({
    queryKey: paymentOpsKeys.detail(paymentId),
    queryFn: () => httpClient.get<Payment>(`/payment-ops/${paymentId}`),
    enabled: !!paymentId,
  });
}

export function usePaymentStatus(paymentId: string) {
  return useQuery({
    queryKey: paymentOpsKeys.status(paymentId),
    queryFn: () =>
      httpClient.get<{ status: string }>(`/payment-ops/${paymentId}/status`),
    enabled: !!paymentId,
  });
}

export function usePaymentsList(filters?: PaymentsListFilters) {
  const qs = new URLSearchParams();
  if (filters?.page) qs.set('page', String(filters.page));
  if (filters?.pageSize) qs.set('pageSize', String(filters.pageSize));
  if (filters?.status) qs.set('status', filters.status);
  if (filters?.user_profile_id)
    qs.set('user_profile_id', filters.user_profile_id);
  if (filters?.event_id) qs.set('event_id', filters.event_id);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: paymentOpsKeys.list(filters),
    queryFn: () =>
      httpClient.get<{ data: Payment[]; total: number }>(
        `/payment-ops/${query}`,
      ),
  });
}

export function usePaymentRefunds(paymentId: string) {
  return useQuery({
    queryKey: paymentOpsKeys.refunds(paymentId),
    queryFn: () =>
      httpClient.get<PaymentRefund[]>(`/payment-ops/${paymentId}/refunds`),
    enabled: !!paymentId,
  });
}

// --- Mutation Hooks ---
export function useInitiatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InitiatePaymentData) =>
      httpClient.post<Payment>('/payment-ops/initiate', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: paymentOpsKeys.all });
    },
  });
}

export function useCapturePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CapturePaymentData) =>
      httpClient.post<Payment>('/payment-ops/capture', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: paymentOpsKeys.all });
    },
  });
}

export function useExecutePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ExecutePaymentData) =>
      httpClient.post<Payment>('/payment-ops/execute', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: paymentOpsKeys.all });
    },
  });
}

export function useRefundPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      ...data
    }: RefundData & { paymentId: string }) =>
      httpClient.post<PaymentRefund>(
        `/payment-ops/${paymentId}/refund`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: paymentOpsKeys.detail(variables.paymentId),
      });
      qc.invalidateQueries({
        queryKey: paymentOpsKeys.refunds(variables.paymentId),
      });
    },
  });
}

// ─── Pay outstanding manual charges ──────────────────────────────────────────
//
// Phase 2 of the unified-financial-state work. The user clicks "Pay
// outstanding charges" on their Funds tab; this mutation calls
// `/payment-ops/initiate-manual-charges` which mints a single Stripe
// PaymentIntent for whatever they currently owe on the manual ledger
// (debits − credits, floored at 0). The Stripe Elements form mounts off
// the returned client_secret exactly as it does for event-registration
// payments — same checkout UI, different funding source.
//
// The webhook handler stamps the covered account_adjustments rows as
// settled when payment_intent.succeeded fires, so we invalidate the
// payment-ops subtree on success. The outstanding-balance query
// refetches on /confirm completion (still owed until the webhook lands).

export interface InitiateManualChargesResponse {
  payment_id: string;
  stripe_payment_intent_id: string;
  client_secret: string;
  stripe_publishable_key: string | null;
  amount: number;
  currency: string;
  status: 'pending';
  breakdown: {
    total_debits: number;
    total_credits: number;
    net_owed: number;
    adjustment_ids: string[];
  };
}

export function useInitiateManualCharges() {
  const qc = useQueryClient();
  return useMutation({
    /**
     * Optional `adjustment_ids` subset — pass to settle only specific
     * unsettled adjustments (e.g. org-scoped from the registration
     * gate). Omit to settle every unsettled row across all orgs.
     */
    mutationFn: (input?: { adjustment_ids?: string[] }) =>
      httpClient.post<InitiateManualChargesResponse>(
        '/payment-ops/initiate-manual-charges',
        input ?? {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: paymentOpsKeys.all });
    },
  });
}

/**
 * Generic confirm-PaymentIntent hook for payment-ops flows that aren't
 * tied to a specific event_registration (e.g. manual-charge settlement).
 * Hits `/payment-ops/confirm` with the PI id; the route updates the
 * payment row to status=completed optimistically and (for manual_charge
 * payments) settles the covered account_adjustments rows. Webhook-side
 * processing arrives a few seconds later as the source-of-truth backup.
 *
 * The registration-specific equivalent is
 * `useCompleteRegistrationPayment` in useRegistrationOps; use that one
 * for event-registration captures so the registration row's
 * payment_status flips alongside the payment row.
 */
export interface ConfirmPaymentResponse {
  payment_id: string;
  status: string;
  provider_status: string;
  amount: number;
  currency: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useConfirmPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payment_intent_id }: { payment_intent_id: string }) =>
      httpClient.post<ConfirmPaymentResponse>('/payment-ops/confirm', {
        payment_intent_id,
      }),
    onSuccess: () => {
      // After /confirm lands, the local payment row is completed and
      // (for manual_charge) account_adjustments are settled. Wide
      // invalidation so the outstanding-balance card on the Funds tab
      // refetches and clears immediately.
      qc.invalidateQueries({ queryKey: paymentOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
    },
  });
}

// ─── Manual-ledger history (user-facing) ─────────────────────────────────────
//
// Backs the "Ledger history" section under the Funds tab so the user has
// a visible record of every committee charge / credit on their account —
// settled or not. Without this, a user who pays off a $10 fine sees the
// Stripe charge succeed but has no on-page proof the fine itself was
// recorded against their account or which org filed it.

export interface ManualLedgerEntry {
  id: string;
  type: 'charge' | 'refund' | 'adjustment' | 'comp' | 'credit' | string;
  direction: 'debit' | 'credit';
  amount: number;
  currency: string;
  reason: string;
  notes: string | null;
  organization_name: string | null;
  created_at: string;
  settled_at: string | null;
  settled_by_payment_id: string | null;
}

export function useMyManualLedger(userId?: string) {
  // When `userId` is set, read THAT contestant's ledger via the admin-gated
  // endpoint so the admin Organization-ledger card matches the contestant's.
  return useQuery({
    queryKey: userId
      ? ([...paymentOpsKeys.all, 'manual-ledger', userId] as const)
      : ([...paymentOpsKeys.all, 'manual-ledger'] as const),
    queryFn: () =>
      httpClient.get<{ entries: ManualLedgerEntry[] }>(
        userId
          ? `/payment-ops/admin/users/${userId}/manual-ledger`
          : '/payment-ops/manual-ledger',
      ),
  });
}

/**
 * Get combined registration + payment status from /registration-payment-ops.
 * Useful for the "Pay Now" flow where we need to check if payment is still pending.
 */
export function useRegistrationPaymentStatus(registrationId: string) {
  return useQuery({
    queryKey: [...paymentOpsKeys.all, 'reg-status', registrationId] as const,
    queryFn: () =>
      httpClient.get<{
        registration_id: string;
        payment_id: string | null;
        payment_status: string | null;
        registration_status: string;
        payment_deadline: string | null;
        time_remaining_seconds: number;
        can_retry_payment: boolean;
        total_amount: number | null;
      }>(`/registration-payment-ops/status/${registrationId}`),
    enabled: !!registrationId,
  });
}

export function useAdminUpdatePaymentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      ...data
    }: AdminUpdateStatusData & { paymentId: string }) =>
      httpClient.request(`/payment-ops/${paymentId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: paymentOpsKeys.all });
    },
  });
}
